"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format, isSameDay, addMonths, subMonths } from "date-fns";
import { Appointment, Customer } from "@/types";
import { cn } from "@/lib/utils";

interface AppointmentCalendarViewProps {
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  calendarDays: Date[];
  appointments: Appointment[];
  customers: Customer[];
  onAppointmentClick: (appointment: Appointment) => void;
}

const statusColors: Record<Appointment["status"], string> = {
  PENDING: "bg-orange-500",
  CONFIRMED: "bg-blue-500",
  COMPLETED: "bg-green-500",
  CANCELLED: "bg-red-500",
  IN_PROGRESS: "bg-yellow-500",
  NOT_COMPLETED: "bg-pink-500",
  POSTPONED: "bg-indigo-500",
  "confirmation-pending": "bg-orange-500",
  confirmed: "bg-blue-500",
  completed: "bg-green-500",
  cancelled: "bg-red-500",
  "in-progress": "bg-yellow-500",
  "not-completed": "bg-pink-500",
  postponed: "bg-indigo-500",
  "on-the-way": "bg-cyan-500",
  "no-answer": "bg-gray-500",
  "no-booking": "bg-gray-500",
  "not-available": "bg-purple-500",
};

export function AppointmentCalendarView({
  currentMonth,
  onMonthChange,
  calendarDays,
  appointments,
  customers,
  onAppointmentClick,
}: AppointmentCalendarViewProps) {
  const [expandedDay, setExpandedDay] = useState<Date | null>(null);

  const getAppointmentsForDay = (day: Date) => {
    return appointments.filter((apt) => isSameDay(apt.date, day));
  };

  const getStatusColor = (status: Appointment["status"]) => {
    return statusColors[status] || "bg-gray-500";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <CardTitle>{format(currentMonth, "MMMM yyyy")}</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMonthChange(subMonths(currentMonth, 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMonthChange(new Date())}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onMonthChange(addMonths(currentMonth, 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[600px]">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="text-center font-semibold text-sm p-2">
                {day}
              </div>
            ))}
            {calendarDays.map((day, index) => {
              const dayAppointments = getAppointmentsForDay(day);
              const isToday = isSameDay(day, new Date());
              const isExpanded = expandedDay && isSameDay(day, expandedDay);
              const showAll = isExpanded || dayAppointments.length <= 3;
              const displayedAppointments = showAll
                ? dayAppointments
                : dayAppointments.slice(0, 3);

              return (
                <div
                  key={index}
                  className={cn(
                    "min-h-24 min-w-[60px] border rounded-lg p-2 transition-colors",
                    isToday && "bg-primary/10 border-primary",
                    dayAppointments.length > 0 && "bg-muted/30"
                  )}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-sm">
                      {format(day, "d")}
                    </div>
                    {dayAppointments.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="h-5 w-5 flex items-center justify-center p-0 text-xs"
                      >
                        {dayAppointments.length}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1">
                    <TooltipProvider>
                      {displayedAppointments.map((apt) => {
                        const customer = customers.find(
                          (c) => c.id === apt.customerId
                        );
                        return (
                          <Tooltip key={apt.id}>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  "text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity text-white",
                                  getStatusColor(apt.status)
                                )}
                                onClick={() => onAppointmentClick(apt)}
                              >
                                {customer?.name || "Unknown"}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="space-y-1">
                                <p className="font-semibold">
                                  {customer?.name || "Unknown Customer"}
                                </p>
                                <p className="text-xs capitalize">
                                  {apt.status}
                                </p>
                                <p className="text-xs">
                                  {(apt.type || "").replace(/_/g, " ")}
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </TooltipProvider>
                    {!showAll && dayAppointments.length > 3 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-6 text-xs p-0"
                        onClick={() => setExpandedDay(day)}
                      >
                        +{dayAppointments.length - 3} more
                      </Button>
                    )}
                    {isExpanded && dayAppointments.length > 3 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full h-6 text-xs p-0"
                        onClick={() => setExpandedDay(null)}
                      >
                        Show less
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
